import { LanguageType, Location, LocationMapper, TransportModes } from '@nx-smartmonkey/shared/domain';
import { Requests } from '@nx-smartmonkey/shared/helpers';

import { AnalyticsSummary } from '../../domain/analytics/AnalyticsSummary';
import { DriverCredentials } from '../../domain/credentials/DriverCredentials';
import { DriversProject } from '../../domain/driversProject/DriversProject';
import { Vehicle } from '../../domain/vehicle/Vehicle';
import { AnalyticsSummaryMapper } from '../../mappers/analytics/AnalyticsSummaryMapper';
import { DriverCredentialsMapper } from '../../mappers/credentials/DriverCredentialsMapper';
import VehicleProjectMapper from '../../mappers/DriversProjectMapper';
import VehicleMapper from '../../mappers/VehicleMapper';
import { SessionRepository } from '../session/SessionRepository';
import { VehicleRepository } from './VehicleRepository';

export class VehicleRepositoryApi implements VehicleRepository {
  constructor(private requests: Requests, private sessionRepository: SessionRepository) {}

  async getVehicle(): Promise<{ vehicle: Vehicle; project: DriversProject }> {
    const response = await this.requests.get({
      uri: `/drivers/v3/vehicle/details`,
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });

    const { vehicle, ...project } = response.data;

    return {
      vehicle: VehicleMapper.toDomain(vehicle),
      project: VehicleProjectMapper.toDomain(project),
    };
  }

  async deleteAccount(): Promise<void> {
    await this.requests.delete({
      uri: `/drivers/v3/vehicle/account`,
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });
  }

  async updateRegisterLocation({
    lat,
    lng,
    lang,
  }: {
    lat: number;
    lng: number;
    lang?: LanguageType;
  }): Promise<Location> {
    const response = await this.requests.put({
      uri: `/drivers/v3/vehicle/register/location`,
      data: { lat, lng, lang },
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });

    return LocationMapper.toDomain(response.data);
  }

  async retrieveCredentials(): Promise<DriverCredentials> {
    const response = await this.requests.get({
      uri: `/drivers/v3/vehicle/credentials`,
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });

    return DriverCredentialsMapper.toDomain(response.data);
  }

  async updateProject({
    distance_unit,
    stop_duration,
    use_traffic,
    stop_grouping,
    transport_mode,
  }: {
    distance_unit?: string;
    stop_duration?: number;
    use_traffic?: boolean;
    stop_grouping?: boolean;
    transport_mode?: TransportModes;
  }): Promise<DriversProject> {
    const response = await this.requests.put({
      uri: `/drivers/v3/vehicle/project`,
      data: { distance_units: distance_unit, stop_duration, use_traffic, stop_grouping, transport_mode },
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });
    const project = VehicleProjectMapper.toDomain(response.data);
    return project;
  }

  async updateVehicle({ vehicle }: { vehicle: Vehicle }): Promise<Vehicle> {
    const vehicleToRepository = VehicleMapper.toRepository(vehicle);
    const response = await this.requests.put({
      uri: `/drivers/v3/vehicle`,
      data: vehicleToRepository,
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });

    const newVehicle = VehicleMapper.toDomain(response.data);
    return newVehicle;
  }

  async retrieveAnalyticsSummary({
    startDate,
    endDate,
  }: {
    startDate: Date;
    endDate: Date;
  }): Promise<AnalyticsSummary> {
    const response = await this.requests.get({
      uri: `/analytics/drivers/summary`,
      params: {
        start_date: startDate,
        end_date: endDate,
      },
      headers: await this.sessionRepository.getAuthenticationHeader(),
    });

    const analyticsSummary = AnalyticsSummaryMapper.toDomain(response.data);
    return analyticsSummary;
  }
}
